// @ts-nocheck
/**
 * Contact Groups list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + DataTable + shadcn Dialog for Create + ConfirmDialog for Delete).
 * Preserves groupNameRegex (alnum + space + common punctuation) client-side validation, ACL gates,
 * Updated cell renders updated_at (v1 showed created_at).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Loader2, Pencil, Plus, RefreshCw, Trash2, Users, FolderTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, patchApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_CREATE,
  API_CONTACT_GROUP_RENAME,
  API_CONTACT_GROUP_LIST,
  API_CONTACT_GROUP_DELETE,
  API_CONTACT_GROUP_BULK_DELETE,
} from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import { USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { cn } from '@/lib/utils/cn';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const groupNameRegex = /^[a-zA-Z0-9 ]*$/;

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';

const ContactGroupList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [newGroup, setNewGroup] = useState<any>({ name: '' });
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [groupsData, setGroupsData] = useState<any[]>([]);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    setLoading(true);
    getApiCall(API_CONTACT_GROUP_LIST, {})
      .then((resp: any) => setGroupsData(resp || []))
      .catch(onError)
      .finally(() => setLoading(false));
    const user = getUser();
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const refetch = () =>
    getApiCall(API_CONTACT_GROUP_LIST, {})
      .then((resp: any) => setGroupsData(resp || []))
      .catch(onError);

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = () => {
    setRefreshing(true);
    refetch().finally(() => setRefreshing(false));
  };

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const isContactsRole = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead =
    isContactsRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CONTACT_GROUP, 'read');
  const canAdd =
    isContactsRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CONTACT_GROUP, 'add');
  const canDelete =
    isContactsRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CONTACT_GROUP, 'delete');
  const canEdit =
    isContactsRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CONTACT_GROUP, 'edit');

  // DataTable's built-in globalFilter handles name / creator / id search.
  const filteredGroups = groupsData;

  const handleCreateGroup = () => {
    const trimmedName = newGroup.name.trim();
    if (!trimmedName) {
      setValidationError('Group name cannot be empty');
      return;
    }
    setValidationError('');
    setSaving(true);
    const renaming = Boolean(newGroup.id);
    (renaming
      ? patchApiCall(API_CONTACT_GROUP_RENAME.replace(':id', newGroup.id), { group_name: trimmedName })
      : postApiCall(API_CONTACT_GROUP_CREATE, { group_name: trimmedName })
    )
      .then(() => {
        toast.success(renaming ? 'Contact Group renamed' : 'Contact Group created successfully');
        setShowCreateModal(false);
        setNewGroup({ name: '' });
        refetch();
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || (renaming ? 'Failed to rename group' : 'Failed to create group'));
        }
      })
      .finally(() => setSaving(false));
  };

  const confirmBulkDelete = async () => {
    const { ids, clear } = pendingDelete;
    setDeleting(true);
    try {
      const res: any = await postApiCall(API_CONTACT_GROUP_BULK_DELETE, { ids: ids.map(Number) });
      const n = Number(res?.deleted_contacts) || 0;
      if (res?.deleted) {
        toast.success(
          `${res.deleted} group(s) deleted${n ? `, ${n} contact${n === 1 ? '' : 's'} deleted` : ''}`,
        );
      }
      for (const b of res?.blocked || []) {
        const shown = b.campaigns.slice(0, 3).join(', ');
        const more = b.campaigns.length > 3 ? ` and ${b.campaigns.length - 3} more` : '';
        toast.error(`"${b.group_name}" not deleted: used in active campaign(s) ${shown}${more}`);
      }
      clear();
      setPendingDelete(null);
      await refetch();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Error deleting groups');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    if (pendingDelete.ids) return confirmBulkDelete();
    setDeleting(true);
    try {
      const res: any = await postApiCall(API_CONTACT_GROUP_DELETE.replace(':id', pendingDelete.id), {});
      const n = Number(res?.deleted_contacts) || 0;
      toast.success(`Contact Group deleted successfully${n ? `, ${n} contact${n === 1 ? '' : 's'} deleted` : ''}`);
      setPendingDelete(null);
      await refetch();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Error deleting group');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'contact-groups-list',
        data: filteredGroups,
        columns: [
          { header: 'Group Id', accessor: 'id' },
          { header: 'Group Name', accessor: (row: any) => row.group_name || '-' },
          { header: 'Members', accessor: (row: any) => row.member_count || 0 },
          {
            header: 'Created By',
            accessor: (row: any) => row.group_creator?.username || '-',
          },
          { header: 'Created At', accessor: (row: any) => formatDate(row.created_at) },
          { header: 'Updated At', accessor: (row: any) => formatDate(row.updated_at) },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            #{row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'group_name',
        header: 'Group Name',
        size: 260,
        cell: ({ row }) => {
          const nm = row.original.group_name || '—';
          const initials = (nm || '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w: string) => w[0])
            .join('')
            .toUpperCase() || '—';
          return (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
                {initials}
              </span>
              <Link
                to={`/contact-group/${row.original.id}`}
                state={{ group: row.original }}
                className="font-medium text-foreground hover:text-primary hover:underline"
              >
                {nm}
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: 'member_count',
        header: 'Members',
        size: 110,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
            <Users className="h-3 w-3" />
            <span className="tabular-nums">{row.original.member_count || 0}</span>
          </span>
        ),
      },
      {
        accessorFn: (row: any) => row.group_creator?.username,
        id: 'group_creator',
        header: 'Created By',
        cell: ({ row }) => (
          <span className="text-sm text-foreground">
            {row.original.group_creator?.username || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {formatDate(row.original.updated_at)}
          </span>
        ),
      },
    ];

    if (canDelete || canEdit) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            {canEdit && (
              <button
                type="button"
                title={`Rename ${row.original.group_name}`}
                onClick={() => {
                  setNewGroup({ id: row.original.id, name: row.original.group_name || '' });
                  setShowCreateModal(true);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                title={`Delete ${row.original.group_name}`}
                onClick={() => setPendingDelete(row.original)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [canDelete, canEdit]);

  // Stat strip data
  const totalGroups = groupsData.length;
  const totalMembers = groupsData.reduce(
    (sum: number, g: any) => sum + (Number(g.member_count) || 0),
    0,
  );

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Contact Groups" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading && groupsData.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Contact Groups"
        description="Grouped phone books used to feed campaigns and lead assignments."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={'h-4 w-4 ' + (refreshing ? 'animate-spin' : '')} />
            </Button>
            {groupsData.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                New Group
              </Button>
            )}
          </>
        }
      />

      {/* Stat strip — Total groups / Total members */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FolderTree className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">Total groups</span>
          <span className="font-semibold text-foreground tabular-nums">{totalGroups}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-sky-900/60 dark:bg-sky-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
            <Users className="h-3.5 w-3.5" />
          </span>
          <span className="text-sky-700 dark:text-sky-300">Total members</span>
          <span className="font-semibold text-sky-800 tabular-nums dark:text-sky-200">
            {totalMembers}
          </span>
        </div>
      </div>

      <DataTable
        tableId="contact-group.list:v1"
        columns={columns}
        data={filteredGroups}
        isLoading={loading}
        searchPlaceholder={
          isSuperAdmin
            ? 'Search by group name, ID, or creator…'
            : 'Search by group name or ID…'
        }
        emptyTitle="No contact groups"
        emptyDescription="Create a group to start uploading contacts."
        getRowId={(row: any) => String(row.id)}
        enableRowSelection={canDelete}
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            variant="destructive"
            disabled={deleting}
            onClick={() => setPendingDelete({ ids, clear })}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete ({ids.length})
          </Button>
        )}
        initialColumnPinning={canDelete || canEdit ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      {/* Create modal */}
      <Dialog
        open={showCreateModal}
        onOpenChange={(o) => {
          if (!o) {
            setShowCreateModal(false);
            setNewGroup({ name: '' });
            setValidationError('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              {newGroup.id ? 'Rename contact group' : 'New contact group'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="group_name">
              Group name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="group_name"
              type="text"
              value={newGroup.name}
              onChange={(e) => {
                if (!groupNameRegex.test(e.target.value)) return;
                setNewGroup({ ...newGroup, name: e.target.value });
                if (validationError) setValidationError('');
              }}
              placeholder="Enter group name (letters, digits and spaces only)"
              maxLength={100}
              className={cn(
                validationError && 'border-destructive focus-visible:ring-destructive',
              )}
            />
            {validationError && (
              <p className="text-xs text-destructive">{validationError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setNewGroup({ name: '' });
                setValidationError('');
              }}
            >
              Discard
            </Button>
            <Button onClick={handleCreateGroup} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {newGroup.id ? (saving ? 'Saving…' : 'Save') : saving ? 'Creating…' : 'Create group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}
        title={pendingDelete?.ids ? 'Delete selected contact groups?' : 'Delete this contact group?'}
        description={
          <>
            {pendingDelete?.ids ? (
              <span className="font-medium text-foreground">
                {pendingDelete.ids.length} selected group(s)
              </span>
            ) : (
              <>
                Group{' '}
                <span className="font-medium text-foreground">
                  {pendingDelete?.group_name}
                </span>
              </>
            )}{' '}
            will be deleted, along with the contacts that were added or uploaded directly into it.
            <br />
            <span className="mt-2 block">
              Note: contacts imported from other groups, and contacts that are also in another
              group, are not deleted and remain in your contacts. Groups used in an active
              campaign are not deleted.
            </span>
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default ContactGroupList;
